import { CardActionArea, CardProps, Chip, Stack, SxProps } from "@mui/material";
import { styled, useTheme } from "@mui/material/styles";
import React, { useCallback, useMemo } from "react";

import { useBasicData } from "@/hooks";

import type { WorkspaceModelFragment } from "../../models/models.graphql";
import { Card, CardContent, CardHeader, CardMedia } from "../Card2";
import { ImageGrid } from "../ImageGrid/ImageGrid";
import { ModelCardContent } from "./components/ModelCardContent/ModelCardContent";
import { INDICATOR_CLASS } from "./components/ModelIndicators/ModelCardIndicators";
import {
  CLICKABLE_LABEL_CLASS,
  SYSTEM_LABEL_NAMES,
  SystemLabelName,
} from "./constants";
import { ModelCardFragment, ModelCardLabelFragment } from "./models.graphql";

/** Model type accepted by ModelCard (allows use from list that uses WorkspaceModel fragment). */
export type ModelCardModel = ModelCardFragment | WorkspaceModelFragment;

const getSystemLabelColor = (labelName: SystemLabelName) => {
  if (labelName.toLowerCase() === "unstable") {
    return "error";
  }

  if (labelName.toLowerCase() === "leaving soon") {
    return "warning";
  }
  return "success";
};

const clickableLabelSx: SxProps = {
  cursor: "pointer",
  "&.MuiChip-clickable:hover": {
    backgroundColor: "success.main",
  },
};

const warningClickableLabelSx: SxProps = {
  cursor: "pointer",
  "&.MuiChip-clickable:hover": {
    backgroundColor: "warning.main",
  },
};

const StyledCard = styled(Card)({
  containerType: "inline-size",
});

interface ModelCardProps<M extends ModelCardModel = ModelCardModel>
  extends CardProps {
  disabled?: boolean;
  model: M;
  onClickCard: (event: React.MouseEvent, model: M) => void;
  onClickLabel?: (event: React.MouseEvent, labelId: string) => void;
}

export const ModelCard = <M extends ModelCardModel = ModelCardModel>(
  props: ModelCardProps<M>,
) => {
  const { disabled, model, onClickCard, onClickLabel, ...rest } = props;

  const theme = useTheme();
  const { workspace } = useBasicData();

  const isUserWorkspaceModel = model.workspace?.id === workspace?.id;

  const modelThumbnail =
    model.featuredFiles?.[0]?.file?.thumbnails?.large?.url ||
    model.featuredFiles?.[0]?.file?.thumbnails?.medium?.url ||
    model.featuredFiles?.[0]?.file?.url;

  const labels = useMemo(
    () =>
      model.labels?.reduce(
        (acc, label) => {
          if (label.isSystem && SYSTEM_LABEL_NAMES.includes(label.name)) {
            acc.systemLabels.push(label);
          } else {
            acc.modelLabels.push(label);
          }
          return acc;
        },
        {
          systemLabels: [] as ModelCardLabelFragment[],
          modelLabels: [] as ModelCardLabelFragment[],
        },
      ),
    [model],
  );

  const handleLabelClick = useCallback(
    (e: React.MouseEvent, labelId: string) => {
      e.stopPropagation();
      onClickLabel?.(e, labelId);
    },
    [onClickLabel],
  );

  const handleLabelClickById = useCallback(
    (labelId: string) => (e: React.MouseEvent) => {
      handleLabelClick(e, labelId);
    },
    [handleLabelClick],
  );

  const handleCardClick = useCallback(
    (e: React.MouseEvent) => {
      if (!disabled) {
        onClickCard(e, model);
      }
    },
    [disabled, model, onClickCard],
  );

  return (
    <StyledCard {...rest}>
      <CardActionArea
        disabled={disabled}
        onClick={handleCardClick}
        sx={{
          [`&:has(.${CLICKABLE_LABEL_CLASS}:hover), &:has(.${INDICATOR_CLASS}:hover)`]:
            {
              "& .MuiCardActionArea-focusHighlight": {
                opacity: 0,
              },
            },
        }}
      >
        <CardMedia>
          <ImageGrid
            imageSrcs={modelThumbnail ? [modelThumbnail] : []}
            rows={1}
            columns={1}
            slotProps={{
              placeholder: {
                sx: {
                  color: theme.palette.secondary[5],
                },
              },
            }}
          />
        </CardMedia>
        <CardHeader
          subheader={
            <ModelCardContent
              model={model}
              modelLabels={labels.modelLabels}
              onClickLabel={onClickLabel}
              isUserWorkspaceModel={isUserWorkspaceModel}
            />
          }
          title={model.name}
        />
        {labels.systemLabels.length > 0 && (
          <CardContent>
            <Stack direction="row" gap={2}>
              {labels.systemLabels.map((label) => {
                const color = getSystemLabelColor(
                  label.name as SystemLabelName,
                );
                const labelClickableSx =
                  color === "warning"
                    ? warningClickableLabelSx
                    : clickableLabelSx;
                return (
                  <Chip
                    key={label.id}
                    className={onClickLabel ? CLICKABLE_LABEL_CLASS : undefined}
                    color={color}
                    label={label.name}
                    variant="pill"
                    onClick={
                      onClickLabel
                        ? handleLabelClickById(String(label.id))
                        : undefined
                    }
                    sx={onClickLabel ? labelClickableSx : undefined}
                  />
                );
              })}
            </Stack>
          </CardContent>
        )}
      </CardActionArea>
    </StyledCard>
  );
};
